import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import {
  User,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut as firebaseSignOut,
  onAuthStateChanged,
} from 'firebase/auth';
import { auth } from '../lib/firebase';

export interface AuthContextValue {
  user: User | null;
  loading: boolean;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signUpWithEmail: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  loginAsDemoUser: () => Promise<void>;
  isDemoUser: boolean;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isDemoUser, setIsDemoUser] = useState(false);

  useEffect(() => {
    let mounted = true;
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (mounted) {
        setUser(currentUser);
        setLoading(false);
      }
    });

    // Fallback: If firebase doesn't respond in 3 seconds, stop loading
    const timeout = setTimeout(() => {
      if (mounted && loading) {
        setLoading(false);
      }
    }, 3000);

    return () => {
      mounted = false;
      unsubscribe();
      clearTimeout(timeout);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Helper to race a promise against a timeout
  const withTimeout = async (promise: Promise<any>, ms = 5000) => {
    let timeoutId;
    const timeoutPromise = new Promise((_, reject) => {
      timeoutId = setTimeout(() => reject(new Error('Request timed out. Check your connection or try Demo Mode.')), ms);
    });
    try {
      await Promise.race([promise, timeoutPromise]);
    } finally {
      clearTimeout(timeoutId);
    }
  };

  const signInWithEmail = async (email: string, password: string) => {
    await withTimeout(signInWithEmailAndPassword(auth, email, password));
  };

  const signUpWithEmail = async (email: string, password: string) => {
    await withTimeout(createUserWithEmailAndPassword(auth, email, password));
  };

  const signInWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    await withTimeout(signInWithPopup(auth, provider));
  };

  const loginAsDemoUser = async () => {
    setUser({
      uid: 'demo-user',
      email: 'demo@intpcat.net',
      displayName: 'Demo Cat',
      photoURL: '/img/profile_intp.png',
      emailVerified: true,
      isAnonymous: true,
      metadata: {},
      providerData: [],
      refreshToken: '',
      tenantId: null,
      delete: async () => { },
      getIdToken: async () => 'demo-token',
      getIdTokenResult: async () => ({
        token: 'demo-token',
        expirationTime: '',
        authTime: '',
        issuedAtTime: '',
        signInProvider: 'custom',
        signInSecondFactor: null,
        claims: {},
      }),
      reload: async () => { },
      toJSON: () => ({}),
      phoneNumber: null,
      providerId: 'custom',
    } as unknown as User);
    setIsDemoUser(true);
    setLoading(false);
  };

  const signOut = async () => {
    if (isDemoUser) {
      setUser(null);
      setIsDemoUser(false);
    } else {
      await firebaseSignOut(auth);
    }
  };

  const value: AuthContextValue = {
    user,
    loading,
    signInWithEmail,
    signUpWithEmail,
    signInWithGoogle,
    signOut,
    loginAsDemoUser,
    isDemoUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
